import { useState } from 'react';

import { useNavigate } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';

import { showAssignmentSuccessToast } from '@/utils/training-plans/show-assignment-success-toast';

import AddTeamMembersPopUp from '@/components/popups/AddTeamMemberPopUp/AddTeamMembersPopUp';
import { usePatchAutoAssign } from '@/components/popups/AddTeamMemberPopUp/usePatchAutoAssign';
import DueDatePopUp from '@/components/popups/DueDatePopUp';

import { routes } from '@/constants';

import { Plan } from '@/_generated/api-clients/xp';
import { useAssignTeamMembers } from '@/routes/TrainingPlans/hooks/useAssignTeamMembers';

interface AddTeamMembersProps {
  plan?: Plan;
  selectedId: string;
  setSelectedId: (id: string) => void;
  setShowAddTeamMembersPopup: (show: boolean) => void;
  setShowDueDatePopup: (show: boolean) => void;
  showAddTeamMembersPopup: boolean;
  showDueDatePopup: boolean;
}

/* ManagedPlansTab -> PlanCards */
const AddTeamMembers: React.FC<AddTeamMembersProps> = ({
  plan,
  selectedId,
  showAddTeamMembersPopup,
  showDueDatePopup,
  setSelectedId,
  setShowDueDatePopup,
  setShowAddTeamMembersPopup,
}) => {
  const { t } = useTranslation();
  const { autoAssignPlansEpic: autoAssignPlansEpicFF } = useFlags();
  const navigate = useNavigate();
  const { assignTeamMembers } = useAssignTeamMembers();

  const isCompliance =
    plan &&
    (plan.type.toLocaleLowerCase() === 'compliance' ||
      plan.type.toLocaleLowerCase() === 'anchor');

  const patchAutoAssign = usePatchAutoAssign();

  const [autoAssignSelected, setAutoAssignSelected] = useState(false);

  const onAssignTeamMembers = async ({
    dueDate,
    members,
    planId,
  }: {
    dueDate: string | null;
    members: string | string[];
    planId: string;
  }) => {
    if (!plan) {
      datadogRum.addError(new Error('No plan found'));
      return;
    }

    const userIds = Array.isArray(members) ? members : [members];

    if (autoAssignPlansEpicFF && autoAssignSelected !== plan.autoAssign) {
      try {
        await patchAutoAssign.mutateAsync({
          path: { planId: plan.id },
          body: {
            autoAssign: autoAssignSelected,
            ...(autoAssignSelected ? { dueDateOffsetInDays: 14 } : {}),
          },
        });
      } catch {
        // Hook already notified Datadog
      }

      if (members.length === 0) {
        navigate(
          { pathname: routes.plans.managePlan({ planId }) },
          { state: { refetch: true } },
        );
        return;
      }
    }

    try {
      await assignTeamMembers(
        dueDate ? { planId, userIds, dueDate } : { planId, userIds },
      );
      const count = userIds.length;
      showAssignmentSuccessToast(count, t);
    } catch (err) {
      datadogRum.addError(err);
    }

    navigate(
      { pathname: routes.plans.managePlan({ planId }) },
      { state: { refetch: true } },
    );
  };

  const onSave = (dueDate: string | null, id?: string) => {
    const idsToProcess = id ?? selectedId;
    setShowDueDatePopup(false);
    if (!plan?.id) {
      datadogRum.addError(
        new Error('Missing plan id while assigning team members'),
      );
      return;
    }
    onAssignTeamMembers({
      dueDate,
      members: idsToProcess.includes(',')
        ? idsToProcess.split(',')
        : idsToProcess,
      planId: plan.id,
    });
  };

  const handleNextClick: React.ComponentProps<
    typeof AddTeamMembersPopUp
  >['handleNextClick'] = ({ id, autoAssign }) => {
    if (!id) return;
    setSelectedId(id);
    setAutoAssignSelected(autoAssign);
    if (!isCompliance) {
      setShowAddTeamMembersPopup(false);
      setShowDueDatePopup(true);
    } else if (id) {
      const offset = plan.dueDateOffsetInDays ?? 0;
      const withOffset = new Date();
      withOffset.setDate(withOffset.getDate() + offset);
      onSave(withOffset.toISOString(), id);
    } else return;
  };

  return (
    <>
      <AddTeamMembersPopUp
        handleNextClick={handleNextClick}
        isOpen={showAddTeamMembersPopup}
        onClose={() => setShowAddTeamMembersPopup(false)}
        plan={showAddTeamMembersPopup && plan ? plan : undefined}
        reassignEligible
        teamMemberQueryOptions={{
          refetchOnMountOrArgChange: true,
        }}
        showAutoAssignToggle
        isCompliance={isCompliance}
      />
      <DueDatePopUp
        isOpen={showDueDatePopup}
        onClose={() => setShowDueDatePopup(false)}
        onSave={dueDate => {
          const planId = plan?.id;

          if (!planId) {
            datadogRum.addError(
              new Error('Missing plan id while assigning team members'),
            );
            return;
          }

          setShowDueDatePopup(false);
          onAssignTeamMembers({
            dueDate,
            members: selectedId.includes(',')
              ? selectedId.split(',')
              : selectedId,
            planId,
          });
        }}
      />
    </>
  );
};

export default AddTeamMembers;
